/**
 * B6 · The NEXT level card — the conversion moment.
 *
 * ## Two decisions that make it work
 *
 * 1. **It isn't a modal that closes and gets lost.** The offer lives in a row
 *    (`stage_offer`) with an expiry and dismissal state, so it survives a
 *    reload and reappears until the student decides. A discount that's only
 *    seen once doesn't convert.
 * 2. **It shows even without a price.** If the cashier is off or the
 *    prepayment lane hasn't landed yet, the server sends the offer without a `quote` and the
 *    card degrades to "Continue with the next level" with no price block.
 *    That's the point of the soft edge `billing.core?`: turning off the cashier
 *    can't hijack the offer.
 *
 * The savings arrive in USD and in the secondary currency **with the rate used**, exactly
 * as the server's `UpsellQuote` contract declares. No amount is
 * calculated here: the cents come whole from the server on purpose.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Sparkles, X } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/solid-router";
import { useOffers } from "./use-progression";
import type { PendingOffer } from "./api";

/** How many installments the offer replaces (extracted so `msg` gets a variable). */
function instalmentsLabel(t: ReturnType<typeof useI18n>["t"], count: number): string {
  return t(msg`${count} monthly instalments`);
}

/** Amount in minor unit → text. No float arithmetic on money. */
function money(minor: number, currency: string): string {
  return `${currency} ${(minor / 100).toFixed(2)}`;
}

const OfferCard: Component<{ offer: PendingOffer; onDismiss: () => void }> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const quote = (): PendingOffer["quote"] => props.offer.quote ?? null;
  const nextTitle = (): string => props.offer.next_stage_title ?? "";
  /** Card holder. Extracted so `msg` gets a VARIABLE. */
  const heading = (): string => {
    const title = nextTitle();
    return title ? t(msg`Keep going: ${title}`) : t(msg`Keep going with the next level`);
  };

  return (
    <article
      data-testid="next-stage-offer"
      class="relative rounded-2xl border border-accent/40 bg-accent/5 p-5"
    >
      <button
        type="button"
        data-testid="offer-dismiss"
        class="absolute right-3 top-3 rounded-full p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={t(msg`Dismiss this offer`)}
        onClick={() => props.onDismiss()}
      >
        <X class="size-4" aria-hidden="true" />
      </button>

      <div class="flex items-start gap-3">
        <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
          <Sparkles class="size-5" aria-hidden="true" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="font-semibold">{heading()}</h3>

          <Show
            when={quote()}
            fallback={
              <p class="mt-1 text-sm text-muted-foreground">
                {t(msg`Ask your academy about joining the next level.`)}
              </p>
            }
          >
            {(q) => (
              <dl class="mt-2 space-y-1 text-sm">
                <div class="flex gap-2">
                  <dt class="text-muted-foreground">{t(msg`Price`)}</dt>
                  <dd class="font-medium">{money(q().offer_amount_usd_minor, "USD")}</dd>
                </div>
                <Show when={q().savings_usd_minor > 0}>
                  <div class="flex gap-2">
                    <dt class="text-muted-foreground">{t(msg`You save`)}</dt>
                    <dd class="font-medium">
                      {money(q().savings_usd_minor, "USD")} ({Math.round(q().savings_pct)}%)
                    </dd>
                  </div>
                </Show>
                <Show when={q().secondary_amount_minor != null}>
                  <div class="flex gap-2">
                    <dt class="text-muted-foreground">{t(msg`In local currency`)}</dt>
                    <dd class="font-medium">
                      {money(q().secondary_amount_minor ?? 0, "VES")}
                      <Show when={q().exchange_rate}>
                        {(rate) => {
                          const shown = rate().toFixed(2);
                          return (
                            <span class="ml-1 text-muted-foreground">
                              {t(msg`(rate ${shown})`)}
                            </span>
                          );
                        }}
                      </Show>
                    </dd>
                  </div>
                </Show>
                <Show when={q().installments_replaced > 0}>
                  <div class="flex gap-2">
                    <dt class="text-muted-foreground">{t(msg`Instead of`)}</dt>
                    <dd class="font-medium">{instalmentsLabel(t, q().installments_replaced)}</dd>
                  </div>
                </Show>
              </dl>
            )}
          </Show>

          <Show when={props.offer.valid_until}>
            {(until) => {
              const when = new Date(until()).toLocaleDateString();
              return (
                <p class="mt-2 text-xs text-muted-foreground">{t(msg`Available until ${when}`)}</p>
              );
            }}
          </Show>

          <Button
            class="mt-3"
            data-testid="offer-cta"
            onClick={() => navigate({ to: quote()?.checkout_path ?? "/payments" })}
          >
            {t(msg`I want the next level`)}
          </Button>
        </div>
      </div>
    </article>
  );
};

/** All of the student's active offers. Empty = renders nothing. */
export const NextStageOffers: Component = () => {
  const { query, dismiss } = useOffers();
  return (
    <Show when={(query.data ?? []).length > 0}>
      <div class="space-y-3">
        <For each={query.data ?? []}>
          {(offer) => <OfferCard offer={offer} onDismiss={() => void dismiss(offer.offer_id)} />}
        </For>
      </div>
    </Show>
  );
};
